import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Users, Send, CheckCircle2, Link2, Clock, Mail, Upload, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";

const emptyForm = { last_name: "", first_name: "", email: "" };

export default function TeachersPanel() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tg, setTg] = useState({ configured: false, bot_username: null });

  useEffect(() => {
    api.get(`/telegram/status`).then((r) => setTg(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teachers`);
      setTeachers(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ last_name: t.last_name || t.name || "", first_name: t.first_name || "", email: t.email });
    setOpen(true);
  };

  const save = async () => {
    if (!form.last_name.trim() || !form.first_name.trim() || !form.email.trim()) {
      toast.error("Inserisci cognome, nome ed email");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/teachers/${editing.id}`, form);
      else await api.post(`/teachers`, form);
      toast.success(editing ? "Docente aggiornato" : "Docente aggiunto");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/teachers/${id}`);
      toast.success("Docente eliminato");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/teachers/${id}/approve`);
      toast.success("Docente approvato");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/teachers/${id}/reject`);
      toast.success("Registrazione rifiutata");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [sendingId, setSendingId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/teachers/import-template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modello_docenti.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const downloadGuide = async () => {
    try {
      const res = await api.get(`/teachers/guide.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "guida_telegram_docenti.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/teachers/import`, fd);
      const errPart = data.errors?.length ? ` · ${data.errors.length} righe con errori` : "";
      toast.success(`Import completato: ${data.created} aggiunti, ${data.skipped} già presenti${errPart}`);
      (data.errors || []).slice(0, 5).forEach((x) => toast.warning(x));
      load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sendLink = async (id) => {
    setSendingId(id);
    try {
      await api.post(`/teachers/${id}/send-telegram-link`);
      toast.success("Email con il link Telegram inviata al docente");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSendingId(null);
    }
  };

  const sendAllLinks = async () => {
    setSendingAll(true);
    try {
      const { data } = await api.post(`/teachers/send-telegram-links`);
      if (data.total === 0) {
        toast.info("Tutti i docenti sono già collegati a Telegram");
      } else {
        const extra = data.failed ? ` (${data.failed} non inviate)` : "";
        toast.success(`Email inviate: ${data.sent}/${data.total}${extra}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Docenti</h1>
          <p className="text-muted-foreground mt-1">Anagrafica docenti e contatti email per le notifiche.</p>
        </div>
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="import-file-input"
            onChange={onImportFile}
          />
          <Button variant="outline" data-testid="download-guide-btn" onClick={downloadGuide} className="gap-2 h-10">
            <FileText className="h-4 w-4" /> Guida Telegram
          </Button>
          <Button variant="outline" data-testid="download-template-btn" onClick={downloadTemplate} className="gap-2 h-10">
            <Download className="h-4 w-4" /> Modello Excel
          </Button>
          <Button variant="outline" data-testid="import-teachers-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-2 h-10">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importa Excel
          </Button>
          <Button variant="outline" data-testid="send-links-all-btn" onClick={sendAllLinks} disabled={sendingAll} className="gap-2 h-10">
            {sendingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Invia link a tutti
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-teacher-btn" onClick={openNew} className="gap-2 h-10">
              <Plus className="h-4 w-4" /> Nuovo docente
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="teacher-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Modifica docente" : "Nuovo docente"}</DialogTitle>
              <DialogDescription>Cognome, nome ed email del docente per l'anagrafica e le notifiche.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="teacher-lastname">Cognome</Label>
                  <Input id="teacher-lastname" data-testid="teacher-lastname-input" placeholder="Rossi"
                    value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="teacher-firstname">Nome</Label>
                  <Input id="teacher-firstname" data-testid="teacher-firstname-input" placeholder="Mario"
                    value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="teacher-email">Email</Label>
                <Input id="teacher-email" type="email" data-testid="teacher-email-input" placeholder="mario.rossi@scuola.it"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="save-teacher-btn" onClick={save} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="rounded-md border border-border bg-background overflow-hidden">
        <table className="w-full text-sm" data-testid="teachers-table">
          <thead>
            <tr className="border-b border-border bg-secondary/60 text-left">
              <th className="px-4 py-3 font-semibold">Cognome e Nome</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold w-48">Telegram</th>
              <th className="px-4 py-3 font-semibold w-52">Stato</th>
              <th className="px-4 py-3 font-semibold w-28 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Caricamento…</td></tr>
            ) : teachers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                  <Users className="mx-auto h-8 w-8 mb-2 opacity-50" />
                  Nessun docente in anagrafica.
                </td>
              </tr>
            ) : (
              teachers.map((t) => (
                <tr key={t.id} data-testid={`teacher-row-${t.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40">
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.email}</td>
                  <td className="px-4 py-3">
                    {t.telegram_chat_id ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle2 className="h-4 w-4" /> Collegato
                      </span>
                    ) : tg.configured && tg.bot_username ? (
                      <div className="flex flex-col items-start gap-1">
                        <a
                          href={`https://t.me/${tg.bot_username}?start=${t.telegram_link_code}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`tg-link-${t.id}`}
                          className="inline-flex items-center gap-1 text-primary text-xs font-medium hover:underline"
                        >
                          <Link2 className="h-4 w-4" /> Collega Telegram
                        </a>
                        <Button
                          size="sm" variant="ghost" data-testid={`send-link-${t.id}`}
                          onClick={() => sendLink(t.id)} disabled={sendingId === t.id}
                          className="h-6 px-1 text-xs text-muted-foreground"
                        >
                          {sendingId === t.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                          Invia link via email
                        </Button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                        <Send className="h-3.5 w-3.5" /> Bot non configurato
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {(t.status || "approved") === "pending" ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                        <Clock className="h-3.5 w-3.5" /> In attesa
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Attivo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {(t.status || "approved") === "pending" && (
                        <>
                          <Button size="sm" variant="outline" data-testid={`approve-teacher-${t.id}`} onClick={() => approve(t.id)} className="h-7 px-2 text-xs">
                            Approva
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`reject-teacher-${t.id}`} onClick={() => reject(t.id)} className="h-7 px-2 text-xs text-destructive">
                            Rifiuta
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" data-testid={`edit-teacher-${t.id}`} onClick={() => openEdit(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`delete-teacher-${t.id}`} className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {t.name}?</AlertDialogTitle>
                            <AlertDialogDescription>Il docente verrà rimosso dall'anagrafica.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-teacher-${t.id}`} onClick={() => remove(t.id)}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
